export const EXERCISE_IMAGE_FIELD = "img_exs";

export const EXERCISE_ANIMATION_STYLE = {
  SM: "ex-preview-anim-sm",
  DEFAULT: "ex-preview-anim",
} as const;

export type ExerciseAnimationStyle =
  (typeof EXERCISE_ANIMATION_STYLE)[keyof typeof EXERCISE_ANIMATION_STYLE];

export const EXERCISE_ANIMATION_FOLDER = "exercise-animation";

type ExerciseImageSource = Record<string, unknown> | null | undefined;

function normalizeImageFileValue(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.value === "string") {
      return record.value.trim();
    }
    if (typeof record.file === "string") {
      return record.file.trim();
    }
    if (typeof record.name === "string") {
      return record.name.trim();
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const file = normalizeImageFileValue(item);
      if (file) {
        return file;
      }
    }
  }

  return "";
}

export function collectExerciseImageNames(
  source: ExerciseImageSource,
): string[] {
  if (!source || typeof source !== "object") {
    return [];
  }

  const raw = source[EXERCISE_IMAGE_FIELD];
  if (!Array.isArray(raw)) {
    const single = normalizeImageFileValue(raw);
    return single ? [single] : [];
  }

  const names: string[] = [];
  for (const item of raw) {
    const file = normalizeImageFileValue(item);
    if (file && !names.includes(file)) {
      names.push(file);
    }
  }
  return names;
}

/** Собирает img_exs / img_exs_anim с обёрток workout/prescription (displayExercise, exercise, …). */
export function resolveExerciseMediaSource(
  exercise: ExerciseImageSource,
): ExerciseImageSource {
  if (!exercise || typeof exercise !== "object") {
    return exercise;
  }

  const nestedSources = [
    exercise,
    exercise.displayExercise,
    exercise.exercise,
  ].filter((source): source is Record<string, unknown> => (
    Boolean(source) && typeof source === "object"
  ));

  const merged: Record<string, unknown> = { ...exercise };
  for (const source of nestedSources) {
    if (!merged[EXERCISE_IMAGE_FIELD] && source[EXERCISE_IMAGE_FIELD]) {
      merged[EXERCISE_IMAGE_FIELD] = source[EXERCISE_IMAGE_FIELD];
    }
    if (!merged.img_exs_anim && source.img_exs_anim) {
      merged.img_exs_anim = source.img_exs_anim;
    }
  }

  return merged;
}

export type ExerciseImagePickOptions = {
  /** Второе статичное изображение, если есть (иначе первое). */
  preferSecondImage?: boolean;
};

export function getDefaultExerciseImageIndex(
  imageCount: number,
  options?: ExerciseImagePickOptions,
): number {
  if (options?.preferSecondImage && imageCount > 1) {
    return 1;
  }
  return 0;
}

export function pickExerciseImageFile(
  exercise: ExerciseImageSource,
  options?: ExerciseImagePickOptions,
): string {
  const names = collectExerciseImageNames(resolveExerciseMediaSource(exercise));
  const idx = getDefaultExerciseImageIndex(names.length, options);
  return names[idx] || "";
}

export function pickExerciseAnimationFile(exercise: ExerciseImageSource): string {
  const source = resolveExerciseMediaSource(exercise);
  if (!source || typeof source !== "object") {
    return "";
  }
  return normalizeImageFileValue(source.img_exs_anim);
}

export function buildExerciseImageSrc(file: string, apiUrl?: string): string {
  if (!file) {
    return "";
  }

  if (file.startsWith("/files/")) {
    return file;
  }

  if (/^https?:\/\//.test(file)) {
    try {
      const pathname = new URL(file).pathname;
      if (pathname.startsWith("/files/")) {
        return pathname;
      }
    } catch {
      /* keep absolute external URL */
    }
    return file;
  }

  const suffix = /\.[a-z0-9]+$/i.test(file) ? "" : ".webp";
  return `/files/exercise-technique/ex-preview/${encodeURIComponent(file)}${suffix}`;
}

export function buildExerciseAnimationSrc(
  file: string,
  apiUrl?: string,
  style: ExerciseAnimationStyle = EXERCISE_ANIMATION_STYLE.DEFAULT,
): string {
  if (!file) {
    return "";
  }

  if (file.startsWith("/files/")) {
    return file;
  }

  if (/^https?:\/\//.test(file)) {
    try {
      const pathname = new URL(file).pathname;
      if (pathname.startsWith("/files/")) {
        return pathname;
      }
    } catch {
      /* keep absolute external URL */
    }
    return file;
  }

  const suffix = /\.[a-z0-9]+$/i.test(file) ? "" : ".webp";
  return `/files/${EXERCISE_ANIMATION_FOLDER}/${style}/${encodeURIComponent(file)}${suffix}`;
}

export function resolveExerciseImageSrc(
  exercise: ExerciseImageSource,
  apiUrl?: string,
  options?: ExerciseImagePickOptions,
): string {
  return buildExerciseImageSrc(pickExerciseImageFile(exercise, options), apiUrl);
}

export function resolveExerciseAnimationSrc(
  exercise: ExerciseImageSource,
  apiUrl?: string,
  style: ExerciseAnimationStyle = EXERCISE_ANIMATION_STYLE.DEFAULT,
): string {
  return buildExerciseAnimationSrc(pickExerciseAnimationFile(exercise), apiUrl, style);
}

/** Анимация на экране подхода; если её нет — первое статичное изображение. */
export function resolveExerciseSessionMediaSrc(
  exercise: ExerciseImageSource,
  apiUrl?: string,
  options?: ExerciseImagePickOptions,
  animationStyle: ExerciseAnimationStyle = EXERCISE_ANIMATION_STYLE.DEFAULT,
): string {
  return (
    resolveExerciseAnimationSrc(exercise, apiUrl, animationStyle)
    || resolveExerciseImageSrc(exercise, apiUrl, options)
  );
}
